import React, { useState, useEffect } from 'react';
import './UnifiedCalendarSystem.css';
import { a2aCalendarService } from '../../a2a/services/a2aCalendarService.js';

const UnifiedCalendarSystem = ({ viewMode = 'blank', onViewChange, cycleDayMode = 'normal', tenant_id = 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3' }) => {
  // Dynamic default values - will be set after checking available data
  const [currentDate, setCurrentDate] = useState(new Date()); // Use today's date as default
  const [academicYear, setAcademicYear] = useState('2024-2025');
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Multi-select view modes - use checkboxes instead of single selection
  const [activeViews, setActiveViews] = useState({
    blank: true,
    event: true, // Enable school events by default
    eca: false,
    slp: false
  });
  
  // Available academic years (2020-2021 to 2025-2026 with migrated data)
  const availableYears = [
    '2020-2021',
    '2021-2022',
    '2022-2023',
    '2023-2024',
    '2024-2025',
    '2025-2026'
  ];
  const [calendarData, setCalendarData] = useState({
    holidays: [],
    schoolEvents: [],
    ecaEvents: [],
    slpEvents: [],
    cycleConfig: null
  });
  const [loading, setLoading] = useState(true);

  // Calendar view modes with colors for combined display
  const VIEW_MODES = {
    blank: { label: 'Calendar_Blank', color: '#ff6b6b', description: '基本校曆 (公眾假期)' },
    event: { label: 'Calendar_Event', color: '#4ecdc4', description: '學校活動' },
    eca: { label: 'Calendar_ECA', color: '#45b7d1', description: '課外活動' },
    slp: { label: 'Calendar_SLP', color: '#96ceb4', description: '學習歷程' }
  };

  // Handle view mode checkbox changes
  const handleViewToggle = (viewKey) => {
    const newActiveViews = {
      ...activeViews,
      [viewKey]: !activeViews[viewKey]
    };
    setActiveViews(newActiveViews);
    
    // Notify parent component of view changes
    if (onViewChange) {
      onViewChange(newActiveViews);
    }
  };

  // Initialize calendar with best available data
  useEffect(() => {
    if (!isInitialized) {
      initializeCalendar();
    }
  }, [isInitialized]);

  useEffect(() => {
    if (isInitialized) {
      loadCalendarData();
    }
  }, [academicYear, activeViews, isInitialized]);

  // Validate that current date is within the selected academic year
  useEffect(() => {
    if (isInitialized && !isDateInAcademicYear(currentDate, academicYear)) {
      console.log('Current date', currentDate, 'is outside academic year', academicYear, '- correcting...');
      const startDate = getAcademicYearStartDate(academicYear);
      setCurrentDate(startDate);
    }
  }, [academicYear, currentDate, isInitialized]);

  const loadCalendarData = async () => {
    setLoading(true);
    console.log('Loading calendar data for academic year:', academicYear);
    
    try {
      // Get academic years using A2A service
      const academicYearsResponse = await a2aCalendarService.getAcademicYears({ 
        tenant_id,
        year_code: academicYear
      });

      if (!academicYearsResponse.success || !academicYearsResponse.data || academicYearsResponse.data.length === 0) {
        console.error('Academic year not found:', academicYear);
        setLoading(false);
        return;
      }

      const academicYearData = academicYearsResponse.data[0];
      console.log('Found academic year data:', academicYearData);

      // Load different data based on active views using A2A service
      const promises = [];
      const promiseTypes = [];

      // Always load holidays if blank view is active
      if (activeViews.blank) {
        promises.push(
          a2aCalendarService.getHolidays({ 
            tenant_id,
            academic_year_id: academicYearData.id
          })
        );
        promiseTypes.push('holidays');
      }

      // Load school events and cycle config if event view is active
      if (activeViews.event || activeViews.blank) {
        promises.push(
          a2aCalendarService.getEvents({ 
            tenant_id,
            academic_year_id: academicYearData.id
          })
        );
        promiseTypes.push('events');
        
        promises.push(
          a2aCalendarService.getCycleDays({ 
            tenant_id,
            academic_year_id: academicYearData.id
          })
        );
        promiseTypes.push('cycleDays');
      }

      // Load ECA events if ECA view is active (would need ECA A2A service)
      if (activeViews.eca) {
        // TODO: Implement ECA A2A service
        promises.push(Promise.resolve({ success: true, data: [] }));
        promiseTypes.push('eca');
      }

      // Load SLP events if SLP view is active (would need SLP A2A service)
      if (activeViews.slp) {
        // TODO: Implement SLP A2A service
        promises.push(Promise.resolve({ success: true, data: [] }));
        promiseTypes.push('slp');
      }

      const results = await Promise.all(promises);
      
      let holidaysData = [];
      let schoolEventsData = [];
      let cycleConfigData = null;
      let ecaEventsData = [];
      let slpEventsData = [];

      // Parse results based on promise types
      promiseTypes.forEach((type, index) => {
        const result = results[index];
        if (result && result.success) {
          switch (type) {
            case 'holidays':
              holidaysData = result.data || [];
              break;
            case 'events':
              schoolEventsData = result.data || [];
              break;
            case 'cycleDays':
              cycleConfigData = result.data || null;
              break;
            case 'eca':
              ecaEventsData = result.data || [];
              break;
            case 'slp':
              slpEventsData = result.data || [];
              break;
          }
        }
      });
      
      console.log('Calendar data loaded:', {
        activeViews,
        holidays: holidaysData.length,
        schoolEvents: schoolEventsData.length,
        ecaEvents: ecaEventsData.length,
        slpEvents: slpEventsData.length,
        cycleConfig: cycleConfigData ? 'loaded' : 'null'
      });

      // Check if current month has any events
      const currentMonth = currentDate.getMonth();
      const currentYear = currentDate.getFullYear();
      const monthEvents = schoolEventsData.filter(event => {
        const eventDate = new Date(event.event_date);
        return eventDate.getMonth() === currentMonth && eventDate.getFullYear() === currentYear;
      });
      
      const monthHolidays = holidaysData.filter(holiday => {
        const holidayDate = new Date(holiday.date);
        return holidayDate.getMonth() === currentMonth && holidayDate.getFullYear() === currentYear;
      });

      if (monthEvents.length === 0 && monthHolidays.length === 0) {
        console.log(`No events found for ${currentYear}年${currentMonth + 1}月`);
      } else {
        console.log(`Found ${monthEvents.length} school events and ${monthHolidays.length} holidays for ${currentYear}年${currentMonth + 1}月`);
      }

      // Debug: Show sample events for the first few dates
      if (schoolEventsData.length > 0) {
        console.log('Sample school events:', schoolEventsData.slice(0, 3));
        console.log('First event date format:', schoolEventsData[0]?.event_date);
      }
      
      setCalendarData({
        holidays: holidaysData,
        schoolEvents: schoolEventsData,
        ecaEvents: ecaEventsData,
        slpEvents: slpEventsData,
        cycleConfig: cycleConfigData
      });

    } catch (error) {
      console.error('Error loading calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initialize calendar with the best available academic year and month
  const initializeCalendar = async () => {
    try {
      console.log('Initializing calendar with best available data...');
      
      // Check which academic years have data using A2A service
      const academicYearsResponse = await a2aCalendarService.getAcademicYears({ 
        tenant_id 
      });

      if (!academicYearsResponse.success || !academicYearsResponse.data || academicYearsResponse.data.length === 0) {
        console.error('No academic years found');
        setIsInitialized(true);
        return;
      }

      const academicYearsData = academicYearsResponse.data;

      // Find the academic year that contains today's date
      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth();
      
      let bestAcademicYear = null;
      
      // Check which academic year today falls into
      for (const yearData of academicYearsData) {
        const startYear = parseInt(yearData.year_code.split('-')[0]);
        const endYear = startYear + 1;
        
        // Academic year runs from September (month 8) to August (month 7)
        if (currentYear === startYear && currentMonth >= 8) {
          // September-December of start year
          bestAcademicYear = yearData.year_code;
          break;
        } else if (currentYear === endYear && currentMonth <= 7) {
          // January-August of end year
          bestAcademicYear = yearData.year_code;
          break;
        }
      }
      
      // If today is not in any academic year, find the best available one
      if (!bestAcademicYear) {
        console.log('Today is not in any academic year, finding best available...');
        
        for (const yearData of academicYearsData) {
          console.log(`Checking academic year: ${yearData.year_code}`);
          
          // Check for school events using A2A service
          const eventsResponse = await a2aCalendarService.getEvents({
            tenant_id,
            academic_year_id: yearData.id,
            limit: 1
          });
          
          // Check for public holidays using A2A service
          const holidaysResponse = await a2aCalendarService.getHolidays({
            tenant_id,
            academic_year_id: yearData.id,
            limit: 1
          });
          
          const schoolEvents = eventsResponse.success ? eventsResponse.data : [];
          const holidays = holidaysResponse.success ? holidaysResponse.data : [];
          
          if (schoolEvents && schoolEvents.length > 0) {
            bestAcademicYear = yearData.year_code;
            console.log(`Found academic year with school events: ${bestAcademicYear}`);
            break;
          } else if (holidays && holidays.length > 0) {
            bestAcademicYear = yearData.year_code;
            console.log(`Found academic year with holidays: ${bestAcademicYear}`);
            break;
          }
        }
      }
      
      // Set the best available academic year and keep today's date
      if (bestAcademicYear) {
        setAcademicYear(bestAcademicYear);
        console.log(`Calendar initialized with: ${bestAcademicYear} academic year, showing today's date (${today.toLocaleDateString('zh-HK')})`);
      } else {
        // Fallback to most recent academic year
        const fallbackYear = academicYearsData[0].year_code;
        setAcademicYear(fallbackYear);
        console.log(`Calendar initialized with fallback: ${fallbackYear} academic year, showing today's date`);
      }
      
      setIsInitialized(true);
    } catch (error) {
      console.error('Error initializing calendar:', error);
      setIsInitialized(true);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getCycleDay = (date) => {
    if (!calendarData.cycleConfig || !activeViews.event) return null;
    
    const startDate = new Date(calendarData.cycleConfig.start_date);
    const targetDate = new Date(date);
    
    // Skip weekends and holidays
    if (targetDate.getDay() === 0 || targetDate.getDay() === 6) return null;
    
    const isHoliday = calendarData.holidays.some(holiday => 
      new Date(holiday.date).toDateString() === targetDate.toDateString()
    );
    if (isHoliday) return null;
    
    // Calculate cycle day
    let dayCount = 0;
    const currentDate = new Date(startDate);
    
    while (currentDate < targetDate) {
      if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
        const isSkipDate = calendarData.holidays.some(holiday => 
          new Date(holiday.date).toDateString() === currentDate.toDateString()
        );
        if (!isSkipDate) {
          dayCount++;
        }
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return (dayCount % calendarData.cycleConfig.cycle_length) + 1;
  };

  const getEventsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    const events = [];

    console.log(`Checking events for date: ${dateStr} (academic year: ${academicYear})`);
    console.log('Available school events:', calendarData.schoolEvents.length);
    console.log('Available holidays:', calendarData.holidays.length);

    // Add holidays if blank view is active
    if (activeViews.blank) {
      const holiday = calendarData.holidays.find(h => h.date === dateStr);
      if (holiday) {
        console.log('Found holiday:', holiday);
        events.push({
          type: 'holiday',
          title: holiday.title,
          title_en: holiday.title_en,
          color: VIEW_MODES.blank.color,
          viewType: 'blank'
        });
      }
    }

    // Add school events if event view is active
    if (activeViews.event) {
      const schoolEvent = calendarData.schoolEvents.find(e => e.event_date === dateStr);
      if (schoolEvent) {
        console.log('Found school event:', schoolEvent);
        events.push({
          type: 'school_event',
          title: schoolEvent.title,
          title_en: schoolEvent.title_en,
          color: VIEW_MODES.event.color,
          priority: schoolEvent.priority,
          viewType: 'event'
        });
      }
    }

    // Also show school events when blank view is active (they're part of basic calendar)
    if (activeViews.blank && !activeViews.event) {
      const schoolEvent = calendarData.schoolEvents.find(e => e.event_date === dateStr);
      if (schoolEvent) {
        console.log('Found school event (blank view):', schoolEvent);
        events.push({
          type: 'school_event',
          title: schoolEvent.title,
          title_en: schoolEvent.title_en,
          color: VIEW_MODES.event.color,
          priority: schoolEvent.priority,
          viewType: 'event'
        });
      }
    }

    // Add ECA events if ECA view is active
    if (activeViews.eca) {
      const ecaEvents = calendarData.ecaEvents.filter(e => e.event_date === dateStr);
      ecaEvents.forEach(event => {
        events.push({
          type: 'eca_event',
          title: event.event_title,
          title_en: event.eca_activities?.name_en || event.event_title,
          color: event.eca_activities?.eca_categories?.color_code || VIEW_MODES.eca.color,
          location: event.location,
          time: event.start_time,
          viewType: 'eca'
        });
      });
    }

    // Add SLP events if SLP view is active (when implemented)
    if (activeViews.slp) {
      const slpEvents = calendarData.slpEvents.filter(e => e.event_date === dateStr);
      slpEvents.forEach(event => {
        events.push({
          type: 'slp_event',
          title: event.title,
          title_en: event.title_en,
          color: VIEW_MODES.slp.color,
          viewType: 'slp'
        });
      });
    }

    console.log(`Total events found for ${dateStr}:`, events.length);
    return events;
  };

  // Suggest a better month when current month has no events
  const suggestBetterMonth = () => {
    if (!calendarData.schoolEvents || calendarData.schoolEvents.length === 0) {
      return null;
    }

    // Find months with events
    const monthsWithEvents = new Set();
    calendarData.schoolEvents.forEach(event => {
      const eventDate = new Date(event.event_date);
      const monthKey = `${eventDate.getFullYear()}-${eventDate.getMonth()}`;
      monthsWithEvents.add(monthKey);
    });

    // Find the closest month with events
    const currentMonthKey = `${currentDate.getFullYear()}-${currentDate.getMonth()}`;
    if (monthsWithEvents.has(currentMonthKey)) {
      return null; // Current month has events
    }

    // Find the next month with events
    const sortedMonths = Array.from(monthsWithEvents).sort();
    const currentIndex = sortedMonths.findIndex(month => month >= currentMonthKey);
    
    if (currentIndex >= 0) {
      const [year, month] = sortedMonths[currentIndex].split('-').map(Number);
      return { year, month };
    } else if (sortedMonths.length > 0) {
      // Wrap around to the first month with events
      const [year, month] = sortedMonths[0].split('-').map(Number);
      return { year, month };
    }

    return null;
  };

  // Navigate to a month with events
  const navigateToMonthWithEvents = () => {
    const betterMonth = suggestBetterMonth();
    if (betterMonth) {
      setCurrentDate(new Date(betterMonth.year, betterMonth.month, 1));
      console.log(`Navigated to month with events: ${betterMonth.year}年${betterMonth.month + 1}月`);
    }
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    
    // Check if the new date is within the current academic year
    if (isDateInAcademicYear(newDate, academicYear)) {
      setCurrentDate(newDate);
    } else {
      console.log('Cannot navigate to', newDate, '- outside academic year', academicYear);
      // Optionally, you could wrap around to the other end of the academic year
      // For now, we'll just prevent the navigation
    }
  };

  const handleAcademicYearChange = (newYear) => {
    setAcademicYear(newYear);
    
    // Find the best month in the new academic year
    const newYearEvents = calendarData.schoolEvents.filter(event => {
      const eventDate = new Date(event.event_date);
      const eventYear = eventDate.getFullYear();
      const eventMonth = eventDate.getMonth();
      
      // Check if event is in the new academic year
      const startYear = parseInt(newYear.split('-')[0]);
      const endYear = startYear + 1;
      
      if (eventYear === startYear && eventMonth >= 8) return true;  // September-December
      if (eventYear === endYear && eventMonth <= 7) return true;    // January-August
      return false;
    });
    
    if (newYearEvents.length > 0) {
      // Find the first month with events in the new academic year
      const firstEvent = newYearEvents.sort((a, b) => new Date(a.event_date) - new Date(b.event_date))[0];
      const eventDate = new Date(firstEvent.event_date);
      setCurrentDate(new Date(eventDate.getFullYear(), eventDate.getMonth(), 1));
      console.log(`Changed to academic year ${newYear}, month ${eventDate.getMonth() + 1} (has events)`);
    } else {
      // Default to September of the new academic year
      const startYear = parseInt(newYear.split('-')[0]);
      setCurrentDate(new Date(startYear, 8, 1)); // September 1st
      console.log(`Changed to academic year ${newYear}, defaulting to September`);
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('zh-HK', { 
      year: 'numeric', 
      month: 'long' 
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isWeekend = (date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  // Helper functions for academic year date validation
  const isDateInAcademicYear = (date, academicYearCode) => {
    const startYear = parseInt(academicYearCode.split('-')[0]);
    const endYear = startYear + 1;
    const year = date.getFullYear();
    const month = date.getMonth();
    
    // Academic year runs from September (month 8) to August (month 7)
    if (year === startYear && month >= 8) return true;  // September-December of start year
    if (year === endYear && month <= 7) return true;    // January-August of end year
    return false;
  };

  const getAcademicYearStartDate = (academicYearCode) => {
    const startYear = parseInt(academicYearCode.split('-')[0]);
    return new Date(startYear, 8, 1); // September 1st
  };

  const getAcademicYearEndDate = (academicYearCode) => {
    const startYear = parseInt(academicYearCode.split('-')[0]);
    return new Date(startYear + 1, 7, 31); // August 31st of next year
  };

  if (loading) {
    return <div className="calendar-loading">載入中...</div>;
  }

  const days = getDaysInMonth(currentDate);
  
  // Check if current month has events
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();
  const monthEvents = calendarData.schoolEvents.filter(event => {
    const eventDate = new Date(event.event_date);
    return eventDate.getMonth() === currentMonth && eventDate.getFullYear() === currentYear;
  });
  
  const monthHolidays = calendarData.holidays.filter(holiday => {
    const holidayDate = new Date(holiday.date);
    return holidayDate.getMonth() === currentMonth && holidayDate.getFullYear() === currentYear;
  });
  
  const hasEvents = monthEvents.length > 0 || monthHolidays.length > 0;
  const betterMonth = suggestBetterMonth();

  return (
    <div className="unified-calendar-system">
      <div className="calendar-header">
        <div className="calendar-navigation">
          <button onClick={() => navigateMonth(-1)} className="nav-button">
            ‹
          </button>
          <h2 className="calendar-title">
            {formatDate(currentDate)}
            <span className="academic-year">({academicYear}學年)</span>
          </h2>
          <button onClick={() => navigateMonth(1)} className="nav-button">
            ›
          </button>
        </div>

        <div className="calendar-controls">
          <div className="academic-year-selector">
            <label htmlFor="year-select">學年:</label>
            <select 
              id="year-select"
              value={academicYear} 
              onChange={(e) => handleAcademicYearChange(e.target.value)}
              className="year-select"
            >
              {availableYears.map(year => (
                <option key={year} value={year}>
                  {year}學年
                </option>
              ))}
            </select>
          </div>
          
          <div className="view-mode-selector">
            {Object.entries(VIEW_MODES).map(([key, config]) => (
              <label key={key} className="view-mode-checkbox">
                <input
                  type="checkbox"
                  checked={activeViews[key]}
                  onChange={() => handleViewToggle(key)}
                  className="view-checkbox"
                />
                <span 
                  className="checkbox-label"
                  style={{ borderColor: config.color }}
                >
                  <span 
                    className="color-indicator" 
                    style={{ backgroundColor: config.color }}
                  ></span>
                  {config.label}
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Event availability indicator */}
      {!hasEvents && (
        <div className="calendar-no-events">
          <div className="no-events-message">
            <span>📅 本月暫無活動</span>
            <p>此月份沒有學校活動或公眾假期</p>
            {betterMonth && (
              <button 
                onClick={navigateToMonthWithEvents}
                className="navigate-to-events-btn"
              >
                前往有活動的月份 →
              </button>
            )}
          </div>
        </div>
      )}

      {hasEvents && (
        <div className="calendar-events-summary">
          <span>📅 本月活動: {monthEvents.length} 個學校活動, {monthHolidays.length} 個公眾假期</span>
        </div>
      )}

      <div className="calendar-grid">
        <div className="weekdays">
          {['日', '一', '二', '三', '四', '五', '六'].map(day => (
            <div key={day} className="weekday">{day}</div>
          ))}
        </div>
        
        <div className="calendar-days">
          {days.map((date, index) => {
            if (!date) {
              return <div key={index} className="calendar-day empty"></div>;
            }

            const events = getEventsForDate(date);
            const cycleDay = getCycleDay(date);
            const isCurrentDay = isToday(date);
            const isWeekendDay = isWeekend(date);

            return (
              <div
                key={index}
                className={`calendar-day ${isCurrentDay ? 'today' : ''} ${isWeekendDay ? 'weekend' : ''}`}
              >
                <div className="day-number">
                  {date.getDate()}
                  {cycleDayMode === 'cycle' && cycleDay && (
                    <span className="cycle-day">[{cycleDay}]</span>
                  )}
                </div>
                
                <div className="day-events">
                  {events.map((event, eventIndex) => (
                    <div
                      key={eventIndex}
                      className={`event ${event.type}`}
                      style={{ backgroundColor: event.color }}
                      title={event.title_en || event.title}
                    >
                      {event.title}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="calendar-legend">
        {Object.entries(VIEW_MODES).map(([key, config]) => (
          activeViews[key] && (
            <div key={key} className="legend-item">
              <span className="legend-color" style={{ backgroundColor: config.color }}></span>
              <span>{config.description}</span>
            </div>
          )
        ))}
        {Object.values(activeViews).every(v => !v) && (
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#ccc' }}></span>
            <span>請選擇要顯示的日曆類型</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default UnifiedCalendarSystem;
